import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import app from "../../firebase.js";

const FORMSUBMIT_ENDPOINT =
  "https://formsubmit.co/hadycomtech@gmail.com";

const submitMultipartForm = (fields, files) =>
  new Promise((resolve, reject) => {
    const frameName = `formsubmit-frame-${Date.now()}`;
    const iframe = document.createElement("iframe");
    const form = document.createElement("form");

    iframe.name = frameName;
    iframe.style.display = "none";

    form.method = "POST";
    form.action = FORMSUBMIT_ENDPOINT;
    form.enctype = "multipart/form-data";
    form.target = frameName;
    form.style.display = "none";

    const appendTextField = (name, value) => {
      const input = document.createElement("input");

      input.type = "hidden";
      input.name = name;
      input.value = value ?? "";

      form.appendChild(input);
    };

    Object.entries(fields).forEach(([name, value]) => {
      appendTextField(name, String(value ?? ""));
    });

    try {
      files.forEach(([documentId, file], index) => {
        const fileInput = document.createElement("input");
        const transfer = new DataTransfer();

        transfer.items.add(file);

        fileInput.type = "file";
        fileInput.name =
          `attachment_${index + 1}_${documentId}`;
        fileInput.files = transfer.files;

        form.appendChild(fileInput);
      });
    } catch (error) {
      reject(
        new Error(
          "Votre navigateur n’a pas pu préparer les pièces jointes."
        )
      );

      return;
    }

    let submitted = false;
    let completed = false;

    const cleanup = () => {
      window.setTimeout(() => {
        form.remove();
        iframe.remove();
      }, 1000);
    };

    const completeSubmission = () => {
      if (completed) {
        return;
      }

      completed = true;
      cleanup();
      resolve();
    };

    iframe.addEventListener("load", () => {
      if (submitted) {
        completeSubmission();
      }
    });

    document.body.appendChild(iframe);
    document.body.appendChild(form);

    window.setTimeout(() => {
      submitted = true;
      form.submit();
    }, 100);

    window.setTimeout(completeSubmission, 15000);
  });

const profileLabels = {
  particulier: "Particulier",
  entreprise: "Entreprise",
  etudiant: "Étudiant",
  diaspora: "Diaspora",
};

const financingLabels = {
  "pret-personnel": "Prêt personnel",
  "avance-salaire": "Avance sur salaire",
  equipement: "Achat d’équipement",
  sante: "Santé",
  logement: "Logement",
  mariage: "Mariage",
  "projet-familial": "Projet familial",
  autre: "Autre besoin",
  tresorerie: "Avance de trésorerie",
  salaires: "Paiement des salaires",
  stock: "Achat de stock",
  materiel: "Achat de matériel",
  fournisseurs: "Paiement des fournisseurs",
  developpement: "Développement",
  investissement: "Investissement",
  "frais-universitaires": "Frais universitaires",
  ordinateur: "Achat d’un ordinateur",
  "logement-etudiant": "Logement étudiant",
  "voyage-etudes": "Voyage d’études",
  formation: "Formation",
  "investissement-guinee": "Investissement en Guinée",
  "aide-familiale": "Aide familiale",
  immobilier: "Immobilier",
  commerce: "Commerce",
  agriculture: "Agriculture",
  entrepreneuriat: "Entrepreneuriat",
};

const fieldLabels = {
  firstName: "Prénom",
  lastName: "Nom",
  birthDate: "Date de naissance",
  phone: "Téléphone",
  email: "Adresse e-mail",
  city: "Ville de résidence",

  companyName: "Nom de l’entreprise",
  registrationNumber: "Numéro RCCM",
  taxNumber: "Numéro NIF",
  representativeName: "Nom du représentant",

  requestedAmount: "Montant demandé",
  desiredDuration: "Durée souhaitée",
  durationUnit: "Unité de durée",
  desiredDate: "Date souhaitée",
  financingDescription: "Description du besoin",

  employmentStatus: "Situation professionnelle",
  employerOrActivity: "Employeur ou activité",
  monthlyIncome: "Revenu mensuel net",
  monthlyExpenses: "Charges mensuelles",
  existingDebts: "Crédits ou dettes en cours",
  currentDebtPayment:
    "Remboursements mensuels en cours",

  businessSector: "Secteur d’activité",
  creationYear: "Année de création",
  monthlyRevenue: "Chiffre d’affaires mensuel",
  businessMonthlyExpenses:
    "Charges mensuelles de l’entreprise",
  employeeCount: "Nombre total de salariés",
  repaymentSource: "Source prévue du remboursement",
  concernedEmployees: "Salariés concernés",
  payrollAmount: "Masse salariale à financer",
};

const moneyFields = [
  "requestedAmount",
  "monthlyIncome",
  "monthlyExpenses",
  "currentDebtPayment",
  "monthlyRevenue",
  "businessMonthlyExpenses",
  "payrollAmount",
];

const personalInformationFields = [
  "firstName",
  "lastName",
  "birthDate",
  "phone",
  "email",
  "city",
];

const companyInformationFields = [
  "companyName",
  "representativeName",
  "registrationNumber",
  "taxNumber",
  "phone",
  "email",
  "city",
];

const financingFields = [
  "requestedAmount",
  "desiredDuration",
  "durationUnit",
  "desiredDate",
  "financingDescription",
];

const personalFinancialFields = [
  "employmentStatus",
  "employerOrActivity",
  "monthlyIncome",
  "monthlyExpenses",
  "existingDebts",
  "currentDebtPayment",
];

const companyFinancialFields = [
  "businessSector",
  "creationYear",
  "monthlyRevenue",
  "businessMonthlyExpenses",
  "employeeCount",
  "repaymentSource",
  "concernedEmployees",
  "payrollAmount",
];

// Firestore refuse les valeurs "undefined".
// Cette fonction nettoie les champs facultatifs avant l'enregistrement.
const sanitizeForFirestore = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) =>
      item === undefined ? null : sanitizeForFirestore(item)
    );
  }

  if (
    value !== null &&
    typeof value === "object" &&
    value.constructor === Object
  ) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [
          key,
          sanitizeForFirestore(item),
        ])
    );
  }

  return value;
};

function FinanceReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isConfirmed, setIsConfirmed] =
    useState(false);

  const [testMessage, setTestMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [createdRequest, setCreatedRequest] =
    useState(null);

  // Mémorise que FormSubmit a déjà accepté les pièces jointes.
  // En cas d'erreur Firestore après l'e-mail, un nouveau clic ne renvoie
  // donc jamais les mêmes documents une seconde fois.
  const [emailedRequest, setEmailedRequest] =
    useState(null);

  const [submittedRequest, setSubmittedRequest] =
    useState(null);

  const profile = searchParams.get("profil");
  const financingType = searchParams.get("type");

  const formData = location.state?.formData || {};

  const uploadedDocuments =
    location.state?.uploadedDocuments || {};

  const isCompany = profile === "entreprise";

  const profileLabel =
    profileLabels[profile] || "Non renseigné";

  const financingLabel =
    financingLabels[financingType] ||
    "Non renseigné";

  const hasFormData =
    Object.keys(formData).length > 0;

  const documents = Object.entries(
    uploadedDocuments
  );

  const formatMoney = (value) => {
    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "Non renseigné";
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
      return value;
    }

    return `${new Intl.NumberFormat("fr-FR").format(
      number
    )} GNF`;
  };

  const formatValue = (fieldName, value) => {
    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "Non renseigné";
    }

    if (moneyFields.includes(fieldName)) {
      return formatMoney(value);
    }

    if (fieldName === "existingDebts") {
      return value === "oui" ? "Oui" : "Non";
    }

    if (fieldName === "employmentStatus") {
      const statuses = {
        salarie: "Salarié(e)",
        independant: "Indépendant(e)",
        entrepreneur: "Entrepreneur(e)",
        fonctionnaire: "Fonctionnaire",
        etudiant: "Étudiant(e)",
        "sans-emploi": "Sans emploi",
        autre: "Autre",
      };

      return statuses[value] || value;
    }

    return value;
  };

  const formatFileSize = (size) => {
    if (!size) {
      return "Taille inconnue";
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} Ko`;
    }

    return `${(
      size /
      (1024 * 1024)
    ).toFixed(1)} Mo`;
  };

  const previewDocument = (file) => {
    if (!file) {
      return;
    }

    const documentUrl =
      URL.createObjectURL(file);

    const previewWindow = window.open(
      documentUrl,
      "_blank",
      "noopener,noreferrer"
    );

    if (!previewWindow) {
      URL.revokeObjectURL(documentUrl);

      alert(
        "Le navigateur a bloqué l’ouverture du document. Autorisez les fenêtres contextuelles pour afficher le fichier."
      );

      return;
    }

    setTimeout(() => {
      URL.revokeObjectURL(documentUrl);
    }, 60000);
  };

  const handleConfirmationChange = (event) => {
    setIsConfirmed(event.target.checked);
    setTestMessage("");
    setSubmitError("");
  };

  const handleSubmit = async () => {
    if (
      !isConfirmed ||
      !hasFormData ||
      documents.length === 0 ||
      isSubmitting ||
      submittedRequest
    ) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setTestMessage("");

    let requestToSend = createdRequest;
    let emailWasSent = Boolean(emailedRequest);

    try {
      const currentUser =
        getAuth(app).currentUser;

      if (!currentUser) {
        throw new Error(
          "Votre session a expiré. Reconnectez-vous avant d’envoyer la demande."
        );
      }

      if (!profile || !financingType) {
        throw new Error(
          "Le profil ou le type de financement est manquant. Revenez au début de la demande et sélectionnez-les à nouveau."
        );
      }

      const sanitizedFormData =
        sanitizeForFirestore(formData);

      const documentMetadata = documents.map(
        ([documentId, file]) => ({
          documentId,
          name: file.name,
          type:
            file.type ||
            "application/octet-stream",
          size: file.size || 0,
          storagePath: null,
          downloadURL: null,
          uploadStatus: "en_attente",
        })
      );

      if (!requestToSend) {
        const requestReference =
          `HDCF-${Date.now()
            .toString()
            .slice(-10)}`;

        const requestDocument = await addDoc(
          collection(
            getFirestore(app),
            "financeRequests"
          ),
          {
            reference: requestReference,
            userId: currentUser.uid,
            userEmail:
              currentUser.email ||
              formData.email ||
              "",
            profile,
            profileLabel,
            financingType,
            financingLabel,
            formData: sanitizedFormData,
            documents: documentMetadata,
            documentCount:
              documentMetadata.length,
            status: "en_attente_documents",
            submissionComplete: false,
            consentAccepted: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }
        );

        requestToSend = {
          id: requestDocument.id,
          reference: requestReference,
        };

        setCreatedRequest(requestToSend);
      }

      const emailFields = {
        _subject:
          `Nouvelle demande HDC Finance - ${requestToSend.reference}`,

        _template: "table",
        _captcha: "false",

        _replyto:
          currentUser.email ||
          formData.email ||
          "",

        Référence_du_dossier:
          requestToSend.reference,

        Profil: profileLabel,
        Financement: financingLabel,

        E_mail_du_demandeur:
          currentUser.email ||
          formData.email ||
          "Non renseigné",

        Nombre_de_justificatifs:
          String(documents.length),
      };

      Object.entries(sanitizedFormData).forEach(
        ([fieldName, value]) => {
          emailFields[
            fieldLabels[fieldName] || fieldName
          ] = String(
            formatValue(fieldName, value)
          );
        }
      );

      // Ne transmet les pièces jointes que si elles ne l'ont pas déjà été.
      // Une éventuelle nouvelle tentative sert uniquement à finaliser
      // le statut Firestore.
      if (!emailWasSent) {
        await submitMultipartForm(
          emailFields,
          documents
        );

        emailWasSent = true;
        setEmailedRequest(requestToSend);
      }

      await updateDoc(
        doc(
          getFirestore(app),
          "financeRequests",
          requestToSend.id
        ),
        {
          status: "soumise",
          submissionComplete: true,
          deliveryMethod: "email",
          emailSentAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      setSubmittedRequest(requestToSend);

      setTestMessage(
        `La demande ${requestToSend.reference} a été enregistrée et les justificatifs ont été envoyés par e-mail.`
      );
    } catch (error) {
      console.error(
        "Erreur lors de l’enregistrement de la demande :",
        error
      );

      const firebaseErrorCode = error?.code
        ? ` (${error.code})`
        : "";

      if (requestToSend && emailWasSent) {
        setSubmitError(
          `Les justificatifs de la demande ${requestToSend.reference} ont bien été envoyés par e-mail, mais Firestore n’a pas pu finaliser le statut du dossier. Aucun nouvel e-mail ne sera envoyé si vous réessayez.${firebaseErrorCode}`
        );
      } else if (requestToSend) {
        setSubmitError(
          `La demande ${requestToSend.reference} est enregistrée, mais les justificatifs n’ont pas pu être envoyés. Vous pouvez réessayer sans créer un autre dossier.${firebaseErrorCode}`
        );
      } else {
        setSubmitError(
          `${
            error.message ||
            "Impossible d’enregistrer la demande. Veuillez réessayer."
          }${firebaseErrorCode}`
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() => navigate(-1)}
      >
        ← Retour aux justificatifs
      </button>

      <main style={styles.content}>
        <header style={styles.introduction}>
          <div style={styles.step}>
            Étape 5 sur 5
          </div>

          <h1 style={styles.title}>
            Vérifiez votre dossier
          </h1>

          <p style={styles.subtitle}>
            Relisez les informations et les
            documents ajoutés avant l’envoi
            définitif de votre demande.
          </p>
        </header>

        <div style={styles.summary}>
          <SummaryItem
            label="Profil"
            value={profileLabel}
          />

          <SummaryItem
            label="Financement"
            value={financingLabel}
          />

          <SummaryItem
            label="Montant demandé"
            value={formatMoney(
              formData.requestedAmount
            )}
          />

          <SummaryItem
            label="Documents ajoutés"
            value={documents.length}
          />
        </div>

        {!hasFormData && (
          <div style={styles.warning}>
            <span style={styles.warningIcon}>
              ⚠️
            </span>

            <p style={styles.warningText}>
              Les informations du formulaire ne
              sont plus disponibles. Revenez au
              formulaire et complétez à nouveau
              votre demande.
            </p>
          </div>
        )}

        <section style={styles.reviewCard}>
          <ReviewSection
            number="1"
            title={
              isCompany
                ? "Informations sur l’entreprise"
                : "Informations personnelles"
            }
            fields={
              isCompany
                ? companyInformationFields
                : personalInformationFields
            }
            formData={formData}
            formatValue={formatValue}
          />

          <div style={styles.divider} />

          <ReviewSection
            number="2"
            title="Détails du financement"
            fields={financingFields}
            formData={formData}
            formatValue={formatValue}
          />

          <div style={styles.divider} />

          <ReviewSection
            number="3"
            title="Capacité de remboursement"
            fields={
              isCompany
                ? companyFinancialFields
                : personalFinancialFields
            }
            formData={formData}
            formatValue={formatValue}
          />

          <div style={styles.divider} />

          <div style={styles.sectionHeading}>
            <div style={styles.sectionNumber}>
              4
            </div>

            <div>
              <h2 style={styles.sectionTitle}>
                Pièces justificatives
              </h2>

              <p
                style={
                  styles.sectionDescription
                }
              >
                Vérifiez les documents ajoutés
                avant de confirmer.
              </p>
            </div>
          </div>

          {documents.length > 0 ? (
            <div style={styles.documentsList}>
              {documents.map(
                ([documentId, file]) => (
                  <div
                    key={documentId}
                    style={styles.documentItem}
                  >
                    <div
                      style={
                        styles.documentInformation
                      }
                    >
                      <div
                        style={
                          styles.documentIcon
                        }
                      >
                        📄
                      </div>

                      <div
                        style={
                          styles.documentText
                        }
                      >
                        <strong
                          style={
                            styles.documentName
                          }
                        >
                          {file.name}
                        </strong>

                        <span
                          style={
                            styles.documentDetails
                          }
                        >
                          {formatFileSize(
                            file.size
                          )}{" "}
                          •{" "}
                          {file.type ||
                            "Type inconnu"}
                        </span>
                      </div>
                    </div>

                    <div
                      style={
                        styles.documentActions
                      }
                    >
                      <span
                        style={
                          styles.documentStatus
                        }
                      >
                        Ajouté ✓
                      </span>

                      <button
                        type="button"
                        style={
                          styles.previewButton
                        }
                        onClick={() =>
                          previewDocument(file)
                        }
                      >
                        👁 Voir le document
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div
              style={styles.emptyDocuments}
            >
              Aucun document n’a été transmis.
            </div>
          )}

          <div style={styles.confirmationBox}>
            <label
              style={styles.confirmationLabel}
            >
              <input
                type="checkbox"
                checked={isConfirmed}
                onChange={
                  handleConfirmationChange
                }
                style={styles.checkbox}
              />

              <span
                style={
                  styles.confirmationText
                }
              >
                Je certifie que les informations
                et les documents fournis sont
                exacts et complets. J’autorise
                HDC Finance à les utiliser pour
                l’étude de ma demande.
              </span>
            </label>
          </div>

          {testMessage && (
            <div style={styles.testMessage}>
              <span>ℹ️</span>

              <p
                style={
                  styles.testMessageText
                }
              >
                {testMessage}
              </p>
            </div>
          )}

          {submitError && (
            <div style={styles.errorMessage}>
              <span>⚠️</span>

              <p
                style={
                  styles.errorMessageText
                }
              >
                {submitError}
              </p>
            </div>
          )}

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.previousButton}
              onClick={() => navigate(-1)}
            >
              ← Modifier les justificatifs
            </button>

            <button
              type="button"
              disabled={
                !isConfirmed ||
                !hasFormData ||
                documents.length === 0 ||
                isSubmitting ||
                Boolean(submittedRequest)
              }
              onClick={handleSubmit}
              style={{
                ...styles.submitButton,
                ...(!isConfirmed ||
                !hasFormData ||
                documents.length === 0 ||
                isSubmitting ||
                submittedRequest
                  ? styles.disabledButton
                  : {}),
              }}
            >
              {isSubmitting
                ? emailedRequest
                  ? "Finalisation du dossier en cours…"
                  : createdRequest
                    ? "Envoi des justificatifs en cours…"
                    : "Enregistrement et envoi en cours…"
                : submittedRequest
                  ? "Demande envoyée ✓"
                  : emailedRequest
                    ? "Finaliser le dossier sans renvoyer l’e-mail →"
                  : createdRequest
                    ? "Réessayer l’envoi des justificatifs →"
                    : "Confirmer et envoyer ma demande →"}
            </button>
          </div>

          <p style={styles.modeNotice}>
            Les informations sont enregistrées
            dans Firestore et les justificatifs
            sont transmis par e-mail à HDC
            Finance.
          </p>
        </section>
      </main>

      {submittedRequest && (
        <div
          style={styles.successModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="finance-success-title"
        >
          <div
            style={styles.successModalCard}
          >
            <div
              style={styles.successModalIcon}
            >
              ✓
            </div>

            <span
              style={styles.successModalBadge}
            >
              Demande transmise
            </span>

            <h2
              id="finance-success-title"
              style={styles.successModalTitle}
            >
              Votre demande a bien été soumise
            </h2>

            <p
              style={styles.successModalText}
            >
              Votre dossier a été enregistré
              sous la référence suivante :
            </p>

            <strong
              style={
                styles.successModalReference
              }
            >
              {submittedRequest.reference}
            </strong>

            <p
              style={styles.successModalText}
            >
              Notre équipe va examiner les
              informations et les justificatifs
              fournis. Vous recevrez un retour
              par e-mail dans les meilleurs
              délais.
            </p>

            <button
              type="button"
              style={
                styles.successModalButton
              }
              onClick={() =>
                navigate(
                  "/consulting/finance",
                  {
                    replace: true,
                  }
                )
              }
            >
              Retour à l’accueil Finance →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div style={styles.summaryItem}>
      <span style={styles.summaryLabel}>
        {label}
      </span>

      <strong style={styles.summaryValue}>
        {value}
      </strong>
    </div>
  );
}

function ReviewSection({
  number,
  title,
  fields,
  formData,
  formatValue,
}) {
  const visibleFields = fields.filter(
    (fieldName) => {
      const value = formData[fieldName];

      return (
        value !== "" &&
        value !== null &&
        value !== undefined
      );
    }
  );

  return (
    <section>
      <div style={styles.sectionHeading}>
        <div style={styles.sectionNumber}>
          {number}
        </div>

        <div>
          <h2 style={styles.sectionTitle}>
            {title}
          </h2>

          <p
            style={
              styles.sectionDescription
            }
          >
            Vérifiez les informations
            renseignées.
          </p>
        </div>
      </div>

      {visibleFields.length > 0 ? (
        <div
          style={styles.informationGrid}
        >
          {visibleFields.map(
            (fieldName) => (
              <div
                key={fieldName}
                style={
                  styles.informationItem
                }
              >
                <span
                  style={
                    styles.informationLabel
                  }
                >
                  {fieldLabels[fieldName] ||
                    fieldName}
                </span>

                <strong
                  style={
                    styles.informationValue
                  }
                >
                  {formatValue(
                    fieldName,
                    formData[fieldName]
                  )}
                </strong>
              </div>
            )
          )}
        </div>
      ) : (
        <div
          style={styles.emptyInformation}
        >
          Aucune information disponible.
        </div>
      )}
    </section>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "40px 24px 90px",
    background:
      "linear-gradient(180deg, #070b18 0px, #0b1020 550px, #f8fafc 550px, #f8fafc 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  backButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  content: {
    width: "100%",
    maxWidth: "1100px",
    margin: "70px auto 0",
  },

  introduction: {
    textAlign: "center",
  },

  step: {
    color: "#a78bfa",
    fontSize: "11px",
    fontWeight: "900",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },

  title: {
    margin: "16px 0 0",
    color: "#ffffff",
    fontSize: "clamp(34px, 5vw, 52px)",
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: "900",
  },

  subtitle: {
    margin: "18px auto 0",
    maxWidth: "670px",
    color: "#cbd5e1",
    fontSize: "15px",
    lineHeight: 1.8,
  },

  summary: {
    marginTop: "40px",
    padding: "20px",
    border:
      "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "20px",
    background:
      "rgba(255, 255, 255, 0.07)",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  },

  summaryItem: {
    padding: "18px",
    borderRadius: "14px",
    background:
      "rgba(2, 6, 23, 0.65)",
  },

  summaryLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "11px",
    fontWeight: "700",
  },

  summaryValue: {
    display: "block",
    marginTop: "8px",
    color: "#ffffff",
    fontSize: "15px",
  },

  warning: {
    marginTop: "24px",
    padding: "15px 18px",
    border: "1px solid #fcd34d",
    borderRadius: "14px",
    background: "#fffbeb",
    display: "flex",
    alignItems: "center",
    gap: "11px",
  },

  warningIcon: {
    fontSize: "18px",
  },

  warningText: {
    margin: 0,
    color: "#92400e",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  reviewCard: {
    marginTop: "30px",
    padding: "32px",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    background: "#ffffff",
    boxShadow:
      "0 20px 55px rgba(15, 23, 42, 0.1)",
  },

  sectionHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },

  sectionNumber: {
    width: "42px",
    minWidth: "42px",
    height: "42px",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "900",
  },

  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "21px",
    fontWeight: "900",
  },

  sectionDescription: {
    margin: "7px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  informationGrid: {
    marginTop: "24px",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "14px",
  },

  informationItem: {
    minHeight: "70px",
    padding: "15px",
    border: "1px solid #e2e8f0",
    borderRadius: "13px",
    background: "#f8fafc",
  },

  informationLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "800",
  },

  informationValue: {
    display: "block",
    marginTop: "7px",
    color: "#0f172a",
    fontSize: "13px",
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },

  emptyInformation: {
    marginTop: "22px",
    padding: "17px",
    borderRadius: "12px",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "12px",
  },

  divider: {
    height: "1px",
    margin: "32px 0",
    background: "#e2e8f0",
  },

  documentsList: {
    marginTop: "24px",
    display: "grid",
    gap: "12px",
  },

  documentItem: {
    padding: "15px",
    border: "1px solid #bbf7d0",
    borderRadius: "13px",
    background: "#f0fdf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  documentInformation: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "11px",
  },

  documentIcon: {
    width: "38px",
    minWidth: "38px",
    height: "38px",
    borderRadius: "11px",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  documentText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  documentName: {
    maxWidth: "520px",
    overflow: "hidden",
    color: "#166534",
    fontSize: "12px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  documentDetails: {
    marginTop: "4px",
    color: "#64748b",
    fontSize: "10px",
  },

  documentActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px",
    flexWrap: "wrap",
  },

  documentStatus: {
    color: "#16a34a",
    fontSize: "11px",
    fontWeight: "900",
  },

  previewButton: {
    padding: "9px 13px",
    border: "1px solid #86efac",
    borderRadius: "10px",
    background: "#ffffff",
    color: "#15803d",
    fontSize: "11px",
    fontWeight: "900",
    cursor: "pointer",
  },

  emptyDocuments: {
    marginTop: "22px",
    padding: "17px",
    borderRadius: "12px",
    background: "#fef2f2",
    color: "#dc2626",
    fontSize: "12px",
  },

  confirmationBox: {
    marginTop: "30px",
    padding: "18px",
    border: "1px solid #ddd6fe",
    borderRadius: "15px",
    background: "#f5f3ff",
  },

  confirmationLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    cursor: "pointer",
  },

  checkbox: {
    width: "18px",
    height: "18px",
    marginTop: "2px",
    accentColor: "#7c3aed",
    cursor: "pointer",
  },

  confirmationText: {
    color: "#4c1d95",
    fontSize: "12px",
    lineHeight: 1.7,
    fontWeight: "700",
  },

  testMessage: {
    marginTop: "18px",
    padding: "15px 18px",
    border: "1px solid #93c5fd",
    borderRadius: "13px",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  testMessageText: {
    margin: 0,
    color: "#1e40af",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  errorMessage: {
    marginTop: "18px",
    padding: "15px 18px",
    border: "1px solid #fecaca",
    borderRadius: "13px",
    background: "#fef2f2",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  errorMessageText: {
    margin: 0,
    color: "#b91c1c",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  actions: {
    marginTop: "28px",
    paddingTop: "24px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  previousButton: {
    padding: "14px 20px",
    border: "1px solid #cbd5e1",
    borderRadius: "13px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  submitButton: {
    padding: "15px 22px",
    border: "none",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow:
      "0 12px 26px rgba(37, 99, 235, 0.2)",
  },

  disabledButton: {
    background: "#cbd5e1",
    boxShadow: "none",
    cursor: "not-allowed",
  },

  modeNotice: {
    margin: "16px 0 0",
    color: "#94a3b8",
    fontSize: "10px",
    textAlign: "right",
  },

  successModalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    padding: "24px",
    background:
      "rgba(2, 6, 23, 0.76)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  successModalCard: {
    width: "100%",
    maxWidth: "500px",
    padding: "38px 34px",
    border:
      "1px solid rgba(124, 58, 237, 0.18)",
    borderRadius: "26px",
    background: "#ffffff",
    boxShadow:
      "0 30px 90px rgba(15, 23, 42, 0.35)",
    textAlign: "center",
  },

  successModalIcon: {
    width: "68px",
    height: "68px",
    margin: "0 auto 18px",
    borderRadius: "50%",
    background:
      "linear-gradient(135deg, #22c55e, #16a34a)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: "900",
    boxShadow:
      "0 14px 30px rgba(34, 197, 94, 0.28)",
  },

  successModalBadge: {
    display: "inline-flex",
    padding: "6px 11px",
    borderRadius: "999px",
    background: "#f0fdf4",
    color: "#15803d",
    fontSize: "10px",
    fontWeight: "900",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },

  successModalTitle: {
    margin: "18px 0 0",
    color: "#0f172a",
    fontSize:
      "clamp(25px, 5vw, 34px)",
    lineHeight: 1.15,
    letterSpacing: "-0.035em",
    fontWeight: "900",
  },

  successModalText: {
    margin: "18px auto 0",
    maxWidth: "420px",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.75,
  },

  successModalReference: {
    display: "block",
    margin: "13px auto 0",
    padding: "12px 16px",
    border: "1px solid #ddd6fe",
    borderRadius: "12px",
    background: "#f5f3ff",
    color: "#6d28d9",
    fontSize: "16px",
    letterSpacing: "0.04em",
  },

  successModalButton: {
    width: "100%",
    marginTop: "28px",
    padding: "15px 20px",
    border: "none",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow:
      "0 14px 30px rgba(37, 99, 235, 0.22)",
  },
};

export default FinanceReview;